import { useState, useMemo, useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import {
  spacing,
  borderRadius,
  opacity as opacityTokens,
  status,
  surface,
} from "@atlas-int/tokens";
import { useQuery } from "@apollo/client/react";
import DownloadsTable from "../../components/data/DownloadsTable";
import DatasetModal from "../../components/data/DatasetModal";
import { formatDataPullDate } from "../../components/data/formatDataPullDate";
import {
  GET_DOWNLOADS_DATA_QUERY as downloadsQuery,
  GET_METADATA as metadataQuery,
} from "@atlas-int/data";
import DataSourcesSection from "./DataSourcesSection";
import SurveyWidget from "../../sharedUtilities/SurveyWidget";
import { Text } from "@atlas-int/ui";
import type {
  DownloadsTable as DownloadsTableType,
  GetDownloadsDataQuery,
  GetMetadataQuery,
} from "@atlas-int/data/graphql";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex-grow: 1;
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: ${spacing[12]};
  padding-right: ${spacing[12]};
  box-sizing: border-box;
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const shimmerBg = css`
  background: linear-gradient(
    90deg,
    ${surface.disabled} 25%,
    ${surface.hover} 50%,
    ${surface.disabled} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SKELETON_ROW_COUNT = 7;
const SKELETON_COL_WIDTHS = ["18%", "14%", "20%", "14%", "8%", "14%", "12%"];

const SkeletonContainer = styled.div`
  width: calc(100vw - 130px);
  display: flex;
  flex-direction: column;
  margin-top: ${spacing[4]};
`;

const SkeletonHeaderRow = styled.div`
  display: flex;
  background-color: #CCD4DB;
  border: 1px solid #BFBFBF;
  height: 56px;
`;

const SkeletonHeaderCell = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  border-right: 1px solid #BFBFBF;
  display: flex;
  align-items: center;
  padding: 0 ${spacing[4]};
  &:last-of-type { border-right: none; }
`;

const SkeletonHeaderText = styled.span`
  width: 60%;
  height: 14px;
  border-radius: 2px;
  ${shimmerBg}
`;

const SkeletonBodyRow = styled.div`
  display: flex;
  height: 53px;
  border-left: 1px solid #BFBFBF;
  border-right: 1px solid #BFBFBF;
  border-bottom: 0.8px solid #BFBFBF;
`;

const SkeletonBodyCell = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  border-right: 0.8px solid #BFBFBF;
  display: flex;
  align-items: center;
  padding: 0 ${spacing[4]};
  &:last-of-type { border-right: none; }
`;

const SkeletonBodyContent = styled.span<{ $width?: string }>`
  width: ${({ $width }) => $width ?? "70%"};
  height: 16px;
  border-radius: 2px;
  ${shimmerBg}
`;

const SkeletonFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 0 ${spacing[4]};
`;

const SkeletonFooterText = styled.span`
  width: 80px;
  height: 14px;
  border-radius: 2px;
  ${shimmerBg}
`;

const SkeletonPager = styled.span`
  width: 220px;
  height: 28px;
  border-radius: ${borderRadius.base};
  ${shimmerBg}
`;

const TableSkeleton = () => (
  <SkeletonContainer aria-hidden aria-busy="true" role="status">
    <SkeletonHeaderRow>
      {SKELETON_COL_WIDTHS.map((w, i) => (
        <SkeletonHeaderCell key={i} $width={w}>
          <SkeletonHeaderText />
        </SkeletonHeaderCell>
      ))}
    </SkeletonHeaderRow>
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, r) => (
      <SkeletonBodyRow key={r}>
        {SKELETON_COL_WIDTHS.map((w, c) => (
          <SkeletonBodyCell key={c} $width={w}>
            <SkeletonBodyContent $width={c === 0 ? "85%" : c === 6 ? "60%" : "65%"} />
          </SkeletonBodyCell>
        ))}
      </SkeletonBodyRow>
    ))}
    <SkeletonFooter>
      <SkeletonFooterText />
      <SkeletonPager />
    </SkeletonFooter>
  </SkeletonContainer>
);

const ErrorContainer = styled.div`
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: ${spacing[12]};
  padding-right: ${spacing[12]};
  box-sizing: border-box;
  width: 100%;
`;

const ErrorMessage = styled.div`
  margin-bottom: ${spacing[16]};
  padding: ${spacing[10]} ${spacing[12]};
  color: var(--text-primary, #1a1a1a);
  background-color: color-mix(in srgb, ${status.error} 6%, transparent);
  border: 1px solid color-mix(in srgb, ${status.error} 25%, transparent);
  border-radius: ${borderRadius.base};
  text-align: center;
  line-height: 1.6;
`;

interface DownloadRow {
  originalData: DownloadsTableType;
  "Display Name": string | null | undefined;
  "Data Type (tag)": string;
  "Classification (tag)": string;
  "Complexity? (tag)": string;
  "Product Level": string | number;
  "Year Min": number | null | undefined;
  "Year Max": number | null | undefined;
  "DV File Name": string | null | undefined;
  "DV File Size": string | null | undefined;
  "DV Publication Date": string | null | undefined;
  DOI: string | null | undefined;
}

interface ColumnFilter {
  id: string;
  value: unknown;
}

const DataPage = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<DownloadRow | null>(
    null,
  );
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const {
    loading,
    error,
    data: downloadsData,
  } = useQuery<GetDownloadsDataQuery>(downloadsQuery);

  const { data: metadataData } = useQuery<GetMetadataQuery>(metadataQuery);

  const dataPullDate = useMemo(
    () => formatDataPullDate(metadataData?.metadata?.dataPullDate),
    [metadataData],
  );

  const data = useMemo(() => {
    if (!downloadsData?.downloadsTable) return [];

    const apiData = downloadsData.downloadsTable;

    return apiData.map((item) => {
      // Extract product classifications
      const classifications = [
        item.productClassificationHs92 && "HS92",
        item.productClassificationHs12 && "HS12",
        item.productClassificationHs22 && "HS22",
        item.productClassificationSitc && "SITC",
        item.productClassificationServicesUnilateral && "Services Unilateral",
      ].filter(Boolean);

      const hasComplexityData = item.complexityData === true;
      let dataType = item.tableDataType as string | null | undefined;
      if (!dataType && item.repo) {
        const repoToDataType: Record<string, string> = {
          bilateral: "bilateral",
          unilateral: "unilateral",
          product: "product",
          product_space: "product_space",
          rankings: "rankings",
          classification: "classification",
        };

        dataType =
          repoToDataType[item.repo.toLowerCase()] || item.repo.toLowerCase();
      }

      dataType = dataType || "Other";

      const productLevel = item.productLevel || "N/A";

      return {
        originalData: item,

        "Display Name": item.displayName,
        "Data Type (tag)": dataType,
        "Classification (tag)": classifications.join("/"),
        "Complexity? (tag)": hasComplexityData ? "Yes" : "No",
        "Product Level": productLevel,
        "Year Min": item.yearMin,
        "Year Max": item.yearMax,
        "DV File Name": item.dvFileName,
        "DV File Size": item.dvFileSize,
        "DV Publication Date": item.dvPublicationDate,
        DOI: item.doi,
      };
    });
  }, [downloadsData]);

  const filterOptions = useMemo(() => {
    if (data.length === 0) {
      return {
        dataTypes: [] as string[],
        classifications: [] as string[],
        productLevels: [] as string[],
      };
    }

    return {
      dataTypes: Array.from(new Set(data.map((row) => row["Data Type (tag)"])))
        .filter(Boolean)
        .sort() as string[],
      classifications: Array.from(
        new Set(
          data.flatMap((row) => (row["Classification (tag)"] || "").split("/")),
        ),
      )
        .filter(Boolean)
        .sort() as string[],
      productLevels: Array.from(
        new Set(data.map((row) => row["Product Level"])),
      )
        .filter(Boolean)
        .filter((d) => d !== "N/A")
        .sort() as string[],
    };
  }, [data]);

  const handleColumnFiltersChange = useCallback(
    (newFilters: ColumnFilter[]) => {
      setFilters(newFilters);
    },
    [],
  );

  const handleOpenModal = (row: DownloadRow) => {
    setSelectedDataset(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <ContentContainer>
        <Text variant="heading-1" style={{ marginTop: "6px" }}>
          Atlas Data Downloads
        </Text>
        <Text variant="body">
          The Atlas provides comprehensive international trade data covering
          over 6,000 products across 250 countries and territories.
        </Text>
        <Text variant="body">
          Figures shown throughout the Atlas, in visualisations, country
          profiles, and rankings, may not exactly match those in our bulk data
          downloads. Download files are refreshed at set intervals throughout
          the year to incorporate revised country reporting and corrections,
          which can produce a small gap between the two. The effect on overall
          trade totals is minimal in both.
        </Text>
        <Text variant="body">
          Below, explore and download curated datasets from the Atlas of
          Economic Complexity. Use the column filters to discover specific
          datasets.
        </Text>
      </ContentContainer>

      {loading && (
        <ContentContainer style={{ alignItems: "center" }}>
          <TableSkeleton />
        </ContentContainer>
      )}

      {error && (
        <ErrorContainer>
          <ErrorMessage>
            <Text
              variant="body"
              style={{ fontWeight: 600, marginBottom: spacing[4] }}
            >
              Data downloads are currently not available.
            </Text>
            <Text variant="body">
              In the meantime, datasets can be accessed directly from the{" "}
              <a
                href="https://dataverse.harvard.edu/dataverse/atlas"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                Growth Lab Dataverse
              </a>
              .
            </Text>
          </ErrorMessage>
        </ErrorContainer>
      )}

      {!loading && !error && data.length > 0 && (
        <DownloadsTable
          data={data}
          filterOptions={filterOptions}
          columnFilters={filters}
          onColumnFiltersChange={handleColumnFiltersChange}
          onOpenModal={handleOpenModal}
          dataPullDate={dataPullDate}
        />
      )}

      <DatasetModal
        open={openModal}
        onClose={handleCloseModal}
        dataset={selectedDataset}
      />
      <ContentContainer>
        <DataSourcesSection downloadsData={downloadsData?.downloadsTable} />
      </ContentContainer>
      <SurveyWidget />
    </PageContainer>
  );
};

export default DataPage;
